import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Mi Cuenta es donde se paga: ahí el bloqueo NO se muestra. Las consultas de
 * fondo de la barra superior (campanita, chat de soporte) siguen respondiendo
 * 403 mientras el plan está vencido y, sin esta excepción, reabrían el modal
 * cada ~30 s encima de la pantalla de pago (QA 2026-09-24).
 */
export const isPaymentArea = (pathname: string) =>
  pathname === "/mi-cuenta" || pathname.startsWith("/mi-cuenta/");

/**
 * Pantalla de bloqueo por suscripción vencida (spec 007, US1 / FR-001) y
 * PAYWALL de prueba vencida (spec 031, US4 / FR-009). Vive siempre montada en
 * MenuWrapper (mismo patrón que ModalAccessDenied) y escucha el evento global
 * "subscription-expired" que disparan los interceptores de axios.
 *
 * Los dos motivos que trae el 403 son problemas DISTINTOS y por eso llevan a
 * pantallas distintas (spec 036, US3):
 *
 *   - TRIAL_EXPIRED: la prueba terminó y todavía no hay plan contratado ⇒ hay
 *     que ELEGIR uno. Al comparador.
 *   - SUBSCRIPTION_EXPIRED: ya tiene su plan, solo se le venció ⇒ tiene que
 *     PAGAR EL QUE YA TIENE. Mandarlo al comparador era un callejón sin
 *     salida: el que solo quería renovar terminaba eligiendo plan de nuevo, y
 *     el que ya estaba en el plan más alto no tenía a dónde ir.
 *
 * No es un error seco: lleva directo a pagar (Mi Cuenta queda accesible
 * durante el bloqueo — allowlist del backend) y la reactivación es inmediata
 * al confirmarse el pago.
 */
const SubscriptionBlocked = () => {
  const [visible, setVisible] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // El listener se registra una sola vez: lee la ruta vigente por ref.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Al entrar a Mi Cuenta (por el CTA o por el menú) se cierra si estaba abierto.
  useEffect(() => {
    if (isPaymentArea(pathname)) setVisible(false);
  }, [pathname]);

  useEffect(() => {
    const onExpired = (event: Event) => {
      if (isPaymentArea(pathnameRef.current)) return;
      const detail = (event as CustomEvent).detail;
      setIsTrial(detail?.reason === "TRIAL_EXPIRED");
      setVisible(true);
    };
    window.addEventListener("subscription-expired", onExpired);
    return () => window.removeEventListener("subscription-expired", onExpired);
  }, []);

  if (!visible) return null;

  const title = isTrial ? "Tu prueba terminó" : "Tu plan venció";
  const body = isTrial
    ? "Reactiva tu cuenta eligiendo un plan. Tus datos están intactos y el acceso se restablece apenas confirmes el pago. Durante la prueba solo se emitieron notas de venta."
    : "Paga tu plan y vuelves a trabajar al instante. Puedes pagar con Yape o con tarjeta, sin dejarla guardada. Tus datos están intactos y te esperan.";
  const ctaLabel = isTrial ? "Elegir plan y reactivar" : "Pagar mi plan";
  // `?pagar=1` abre el checkout de renovación al llegar: el bloqueado no
  // tiene que buscar el botón dentro de Mi Cuenta.
  const ctaRoute = isTrial
    ? "/mi-cuenta/planes"
    : "/mi-cuenta/gestionar-suscripcion?pagar=1";

  return (
    <div
      role="alertdialog"
      aria-label="Suscripción vencida"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(23, 15, 60, 0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          maxWidth: "480px",
          width: "100%",
          padding: "2.5rem 2rem",
          textAlign: "center",
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
        }}
      >
        <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🔒</div>
        <h2 style={{ margin: "0 0 0.5rem", color: "#45348E", fontSize: "1.35rem" }}>
          {title}
        </h2>
        <p style={{ margin: "0 0 1.5rem", color: "#4b5563", lineHeight: 1.5 }}>
          {body}
        </p>
        <button
          onClick={() => {
            setVisible(false);
            navigate(ctaRoute);
          }}
          style={{
            background: "#45348E",
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            padding: "0.85rem 1.75rem",
            fontSize: "1rem",
            fontWeight: 600,
            cursor: "pointer",
            width: "100%",
          }}
        >
          {ctaLabel}
        </button>
        <button
          onClick={() => {
            setVisible(false);
            navigate("/mi-cuenta");
          }}
          style={{
            background: "transparent",
            color: "#45348E",
            border: "none",
            marginTop: "0.75rem",
            fontSize: "0.9rem",
            cursor: "pointer",
          }}
        >
          Ir a Mi Cuenta
        </button>
      </div>
    </div>
  );
};

export default SubscriptionBlocked;
